import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Download, Loader2 } from 'lucide-react';
import Header from '../components/Header';
import { api } from '../context/AuthContext';
import { RechtstexteSenden, VersandLog } from '../components/rechtstexte/RechtstexteSenden';
import { Toaster } from '../components/ui/sonner';
import { OffeneListe, NachweisSuche } from '../components/rechtstexte/RechtstexteNachweis';

const fmt = (iso) => (iso ? new Date(iso).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' }) : '—');

const DocKarte = ({ d, gesamt }) => {
  const ohne = d.baseline && !d.angefordert;
  return (
  <div className="bg-white border border-gray-200 p-5 flex-1 min-w-[260px]" data-testid={`rt-doc-${d.doc}`}>
    <p className="text-[10px] tracking-[0.2em] uppercase text-[#b8893d]">{d.baseline ? (d.angefordert ? `Ausgangsversion · bei ${d.angefordert} angefordert` : 'Ausgangsversion') : 'Bestätigung erforderlich'}</p>
    <p className="font-serif text-xl text-[#1B3A5C] mt-1">{d.titel}</p>
    <p className="text-xs text-[#7a8a99] mt-1">Version <span className="font-mono">{d.hash}</span>{d.stand ? ` · Stand ${d.stand}` : ''} · seit {fmt(d.seit)}</p>
    {!d.baseline && <p className="text-xs text-[#7a8a99]">Info-Mail: {d.mail_am ? fmt(d.mail_am) : 'nicht versendet'}</p>}
    <div className="flex gap-6 mt-4">
      <div><p className="font-serif text-2xl text-emerald-700" data-testid={`rt-bestaetigt-${d.doc}`}>{ohne ? '—' : d.bestaetigt}</p><p className="text-[10px] uppercase tracking-wider text-[#7a8a99]">bestätigt</p></div>
      <div><p className="font-serif text-2xl text-rose-700" data-testid={`rt-offen-${d.doc}`}>{ohne ? 0 : d.offen}</p><p className="text-[10px] uppercase tracking-wider text-[#7a8a99]">offen</p></div>
      <div><p className="font-serif text-2xl text-[#1B3A5C]">{gesamt}</p><p className="text-[10px] uppercase tracking-wider text-[#7a8a99]">Portal-Nutzer</p></div>
    </div>
  </div>
  );
};

export default function AdminRechtstexte() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get('/admin/rechtstexte').then((r) => setData(r.data)).catch(() => setData({ docs: [], offene_nutzer: [], versionen: [], versand: [] }));
  useEffect(() => { load(); }, []);
  const exportCsv = async () => {
    setBusy(true);
    try {
      const r = await api.get('/admin/rechtstexte/export.csv', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a'); a.href = url; a.download = 'Rechtstexte_Bestaetigungen.csv'; a.click(); URL.revokeObjectURL(url);
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#f5f8fa]">
      <Toaster />
      <Header />
      <section className="text-[#15202B] pt-[136px] lg:pt-[184px] pb-2">
        <div className="max-w-5xl mx-auto px-6 lg:px-10">
          <div className="flex items-center gap-3 text-[11px] tracking-[0.3em] text-[#e0b56a] uppercase mb-2"><ShieldCheck className="w-4 h-4" /> Rechtstexte</div>
          <h1 className="font-serif text-4xl sm:text-5xl font-light" data-testid="rt-title">AGB & Datenschutz – Bestätigungen</h1>
          <p className="text-[#56616D] text-sm mt-2 max-w-2xl">Jede Änderung der deutschen Fassung erzeugt automatisch eine neue Version. Mandanten müssen sie beim nächsten Login bestätigen und erhalten einmalig eine Info-Mail.</p>
        </div>
      </section>
      <main className="max-w-5xl mx-auto px-6 lg:px-10 py-8 space-y-6">
        {!data ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#b8893d]" /></div> : (<>
          <div className="flex flex-wrap gap-4">{data.docs.map((d) => <DocKarte key={d.doc} d={d} gesamt={data.nutzer_gesamt} />)}</div>
          <RechtstexteSenden onDone={load} />
          <VersandLog items={data.versand || []} />
          <NachweisSuche />
          <div className="bg-white border border-gray-200">
            <div className="flex items-center px-4 py-3 border-b border-gray-100">
              <p className="text-[11px] tracking-[0.2em] uppercase text-[#1B3A5C] font-semibold">Noch nicht bestätigt ({data.offene_nutzer.length})</p>
              <button type="button" onClick={exportCsv} disabled={busy} data-testid="rt-export" className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] tracking-wider border border-[#1B3A5C] text-[#1B3A5C] hover:bg-[#1B3A5C] hover:text-white transition-colors">
                {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} EXPORT (CSV)
              </button>
            </div>
            <OffeneListe nutzer={data.offene_nutzer} onDone={load} />
          </div>
          <div className="bg-white border border-gray-200">
            <p className="px-4 py-3 border-b border-gray-100 text-[11px] tracking-[0.2em] uppercase text-[#1B3A5C] font-semibold">Versionsverlauf</p>
            <ul className="divide-y divide-gray-50">
              {data.versionen.map((v) => (
                <li key={`${v.doc}-${v.hash}`} className="px-4 py-2 text-xs flex flex-wrap gap-3">
                  <span className="w-24 text-[#1B3A5C]">{v.doc === 'agb' ? 'AGB' : 'Datenschutz'}</span><span className="font-mono text-[#34536b]">{v.hash}</span>
                  <span className="text-[#7a8a99]">{v.stand ? `Stand ${v.stand}` : ''}</span><span className="text-[#7a8a99]">{v.baseline ? 'Ausgangsversion' : 'Pflicht-Bestätigung'}</span>
                  <span className="ml-auto text-[#7a8a99]">{fmt(v.erstellt_am)}{v.aktuell ? ' · aktuell' : ''}</span>
                </li>
              ))}
            </ul>
          </div>
        </>)}
      </main>
    </div>
  );
}
